import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, AlertCircle, FileText, TrendingUp } from 'lucide-react';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { taxFilingApi, ApiError } from '@solara/api-client';
import type { TaxFilingDto } from '@solara/api-client';

const URGENT = new Set(['AWAITING_CLIENT_APPROVAL', 'SUBMITTED']);

export default function TaxDashboardPage() {
  const { t } = useTranslation('p1');
  const [filings, setFilings] = useState<TaxFilingDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      setFilings(await taxFilingApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  const next = useMemo(() => {
    const now = new Date();
    return filings
      .filter((f) => new Date(f.dueDate) >= now && f.status !== 'ACCEPTED')
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
      .slice(0, 6);
  }, [filings]);

  const past = useMemo(
    () => filings.filter((f) => f.status === 'ACCEPTED').slice(0, 10),
    [filings],
  );

  const urgent = filings.filter((f) => URGENT.has(f.status));
  const totalsByType = useMemo(() => {
    const totals: Record<string, { net: number; count: number }> = {};
    for (const f of filings) {
      if (!totals[f.filingType]) totals[f.filingType] = { net: 0, count: 0 };
      totals[f.filingType]!.net += Number(f.netPayableMinor) / 100;
      totals[f.filingType]!.count++;
    }
    return totals;
  }, [filings]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('taxFiling.dashboardTitle')}</h1>
        <p className="text-fg-muted text-sm">{t('taxFiling.dashboardSubtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {Object.entries(totalsByType).map(([type, tot]) => (
          <Card key={type} className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-sm">{type}</CardTitle>
            </CardHeader>
            <CardBody>
              <p className="text-2xl font-semibold">{tot.net.toLocaleString()} AED</p>
              <p className="text-xs text-fg-muted">
                {t('taxFiling.netPayableLabel')} · {t('taxFiling.filingsCount', { count: tot.count })}
              </p>
            </CardBody>
          </Card>
        ))}
      </div>

      {urgent.length > 0 && (
        <Card className="border-warning/40 bg-warning/5 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-warning">
              <AlertCircle className="h-4 w-4" />
              {t('taxFiling.urgentTitle')}
            </CardTitle>
          </CardHeader>
          <CardBody>
            <ul className="divide-y divide-border text-sm">
              {urgent.map((f) => (
                <li key={f.id} className="py-2 flex items-center justify-between">
                  <Link to={`/tax/filings/${f.id}`} className="hover:underline font-medium">
                    {f.filingType} · {f.periodStart.slice(0, 10)} → {f.periodEnd.slice(0, 10)}
                  </Link>
                  <Badge
                    tone={f.status === 'AWAITING_CLIENT_APPROVAL' ? 'warning' : 'info'}
                    className="text-[10px]"
                  >
                    {t(`taxFiling.statuses.${f.status}`, { defaultValue: f.status })}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {t('taxFiling.calendar')}
            </CardTitle>
          </CardHeader>
          <CardBody>
            {next.length === 0 ? (
              <p className="py-3 text-sm text-fg-muted">{t('taxFiling.noUpcoming')}</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {next.map((f) => {
                  const daysLeft = Math.ceil(
                    (new Date(f.dueDate).getTime() - Date.now()) / 86_400_000,
                  );
                  return (
                    <li key={f.id} className="flex items-center justify-between">
                      <Link to={`/tax/filings/${f.id}`} className="hover:underline">
                        {f.filingType} · {f.periodStart.slice(0, 10)} → {f.periodEnd.slice(0, 10)}
                      </Link>
                      <span className={daysLeft <= 14 ? 'text-warning text-xs' : 'text-xs text-fg-muted'}>
                        {t('taxFiling.daysAway', { count: daysLeft })}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              {t('taxFiling.pastFilings')}
            </CardTitle>
          </CardHeader>
          <CardBody>
            {past.length === 0 ? (
              <p className="py-3 text-sm text-fg-muted">{t('taxFiling.noPastFilings')}</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {past.map((f) => (
                  <li key={f.id} className="flex items-center justify-between">
                    <span>
                      <FileText className="inline h-3 w-3 me-1" />
                      {f.filingType} · {f.periodStart.slice(0, 10)}
                    </span>
                    <span className="text-xs text-fg-muted">
                      {f.ftaSubmissionId ?? t('taxFiling.ftaIdPending')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
